import React, { useState } from "react";
import { Button, TextField, Typography, Box, Divider } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import { useTranslation } from "react-i18next";

const Auth: React.FC = () => {
  const auth = getAuth();
  const provider = new GoogleAuthProvider();
  const { t } = useTranslation() as { t: (key: string) => string };

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isRegistering, setIsRegistering] = useState(false); // 👈 Toggle state

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegistering) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);
    try {
      await signInWithPopup(auth, provider);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 400, mx: "auto", p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {isRegistering ? t("auth.register") : t("auth.signIn")}
      </Typography>

      <Button
        variant="contained"
        fullWidth
        onClick={handleGoogleLogin}
        disabled={loading}
      >
        {loading ? (
          <CircularProgress size={20} color="inherit" />
        ) : isRegistering ? (
          t("auth.registerWithGoogle")
        ) : (
          t("auth.signInWithGoogle")
        )}
      </Button>

      <Divider sx={{ my: 2 }}>{t("auth.or")}</Divider>

      <form onSubmit={handleEmailSubmit}>
        <TextField
          label={t("auth.email")}
          type="email"
          fullWidth
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label={t("auth.password")}
          type="password"
          fullWidth
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Button type="submit" variant="outlined" fullWidth disabled={loading}>
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : isRegistering ? (
            t("auth.register")
          ) : (
            t("auth.signIn")
          )}
        </Button>
      </form>

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      <Typography variant="body2" sx={{ mt: 2, textAlign: "center" }}>
        {isRegistering ? t("auth.alreadyHaveAccount") : t("auth.noAccount")}{" "}
        <Button variant="text" onClick={() => setIsRegistering(!isRegistering)}>
          {isRegistering ? t("auth.signIn") : t("auth.register")}
        </Button>
      </Typography>
    </Box>
  );
};

export default Auth;
